import React, { useId, useState } from "react";
import type { FaqBlock, FaqItem } from "@/types/product";
import { useLocalizedTexts, t } from "@/utils/translations";
import { sanitizeHtml } from "@/utils/sanitizeHtml";
import shared from "../ProductDetailContent.module.css";
import styles from "./FaqSection.module.css";

function PlusIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M8 3v10M3 8h10"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 8h10"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function FaqRow({ item }: { item: FaqItem }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <div className={styles.item}>
      <button
        type="button"
        className={styles.summary}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{item.question}</span>
        <span className={styles.toggle} aria-hidden="true">
          {open ? <MinusIcon /> : <PlusIcon />}
        </span>
      </button>
      <div className={styles.collapse} data-open={open ? "true" : undefined}>
        <div id={panelId} className={styles.answer} role="region">
          <div
            className={styles.answerInner}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.answer) }}
          />
        </div>
      </div>
    </div>
  );
}

export const FaqSection: React.FC<{ block: FaqBlock }> = ({ block }) => {
  const localizedTexts = useLocalizedTexts();
  const items = block.items ?? [];
  if (!items.length) return null;

  const heading =
    block.heading?.trim() ||
    t(
      localizedTexts,
      "Tracker.Products.FAQHeading",
      "Frequently asked questions"
    );

  return (
    <section className={`${shared.section} ${shared.bgVeryDark}`}>
      <div className={shared.innerTight}>
        <h2 className={styles.heading}>{heading}</h2>
        <div className={styles.list}>
          {items.map((item) => (
            <FaqRow key={item.id} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
